import { site, uuid, assets, dialogs, i18next, linkEditing, clipboard, hdrm } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import imageMimeToExtension from '../../extensions/registry/ws-image/helpers/image-mime-to-extension';
import base64toBlob from '../../utils/base64-to-blob';
import getBlockDataFromClipboard from './get-block-data-from-clipboard';

const { common, linkTypes, defaultConfig } = linkEditing.constants;

const createNewAsset = async (base64, isBackgroundImage) => {
  const blob = await base64toBlob(base64);

  const newAsset = await assets.operations.createMediaGalleryAssets({
    asset: blob,
    name: `${uuid()}.${imageMimeToExtension(blob.type)}`,
    image_placement: isBackgroundImage
      ? assets.constants.IMAGE_PLACEMENTS.BACKGROUND
      : assets.constants.IMAGE_PLACEMENTS.INLINE,
  });

  return newAsset;
};

const provideNewBgImageSrc = async (srcset, blockElement) => {
  const newSrcset = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const part of srcset) {
    // eslint-disable-next-line no-await-in-loop
    const newAsset = await createNewAsset(part.src, true);
    const { source, altSource } = newAsset.data;

    if (part.mediaQuery) {
      newSrcset.push(`${part.mediaQuery} ${source}`);
    } else if (altSource) {
      newSrcset.push(`${altSource}, ${source}`);
    } else {
      newSrcset.push(source);
    }
  }

  const backgroundImageSrc = newSrcset.join(', ');
  blockElement.setAttribute('background-image', backgroundImageSrc);
};

const provideNewAssets = async (items, blockElement) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(items)) {
    // eslint-disable-next-line no-await-in-loop
    const newAsset = await createNewAsset(value);
    const { source } = newAsset.data;
    const itemElement = blockElement.querySelector(`[src='${key}']`);
    itemElement.setAttribute('src', source);
  }
};

const processLinks = (blockElement, isSameSite = false) => {
  const links = blockElement.querySelectorAll('a[ws-link-config]');

  links.forEach((link) => {
    const linkConfig = JSON.parse(link.getAttribute(common.LINK_CONFIG_ATTRIBUTE));

    if (linkConfig?.linkType === linkTypes.ANCHOR) {
      link.setAttribute(common.LINK_CONFIG_ATTRIBUTE, JSON.stringify(defaultConfig));
    }

    if (
      !isSameSite &&
      (linkConfig?.linkType === linkTypes.PAGE || linkConfig?.linkType === linkTypes.DOWNLOAD)
    ) {
      link.setAttribute(common.LINK_CONFIG_ATTRIBUTE, JSON.stringify(defaultConfig));
    }
  });
};

const getBlockFromClipboard = async () => {
  const siteId = site.accessors.getSiteId();
  const activePageId = site.accessors.getActivePageId();

  const blockData = await getBlockDataFromClipboard();

  if (!blockData) {
    return null;
  }

  const { blockElement, parsedData } = blockData;

  if (clipboard.verifiers.hasClipboardElementData()) {
    const { element } = clipboard.accessors.getClipboardData();

    if (element.id === blockElement.id) {
      return null;
    }
  }

  if (siteId !== parsedData.siteId) {
    dialogs.operations.show(dialogTypes.IMAGE_LOADING_DIALOG, {
      captions: {
        title: `${i18next.t('Uploading block')}...`,
        description: i18next.t('Your block will be uploaded to the website soon'),
      },
    });

    processLinks(blockElement);

    if (parsedData?.images) {
      await provideNewAssets(parsedData.images, blockElement);
    }

    if (parsedData?.icons) {
      await provideNewAssets(parsedData.icons, blockElement);
    }

    if (parsedData?.bgImage) {
      await provideNewBgImageSrc(parsedData.bgImage, blockElement);
    }

    if (parsedData?.originalOptionalChildren) {
      parsedData.originalOptionalChildren.forEach((child) => {
        if (!child.isEnabled) {
          hdrm.helpers.updateChildrenPresence(blockElement, child.querySelector, false, child.id);
        }
      });
    }

    dialogs.operations.hide();
  } else if (activePageId !== parsedData.pageId) {
    processLinks(blockElement, true);
  }

  return blockElement;
};

export default getBlockFromClipboard;
